import React, { useState, useEffect } from 'react';
import { Search, Filter, Edit3, Mail, Phone, Calendar, DollarSign, Eye, Star, Users, X, Plus, Download, Trash2, User, TrendingUp, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
// Direct plan fetch (standalone)
const fallbackLimits = { id: 'free', label: 'Free', fans: 200 };

const Fans = () => {
  const { user } = useAuth();
  const [planId, setPlanId] = useState('free');
  const [limits, setLimits] = useState(fallbackLimits);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [selectedFan, setSelectedFan] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTag, setFilterTag] = useState('All');
  const [isAddFanOpen, setIsAddFanOpen] = useState(false);
  const [newFan, setNewFan] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [fans, setFans] = useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [fanToDelete, setFanToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  // Fetch plan directly from Supabase
  useEffect(() => {
    let active = true;
    const loadPlan = async () => {
      if (!user?.id) {
        setPlanId('free');
        setLimits(fallbackLimits);
        setLoadingPlan(false);
        return;
      }
      setLoadingPlan(true);
      try {
        const { data: subscription, error: subError } = await supabase
          .from('subscriptions')
          .select('plan_id')
          .eq('user_id', user.id)
          .maybeSingle();
        if (subError) throw subError;
        if (!subscription?.plan_id) {
          if (active) {
            setPlanId('free');
            setLimits(fallbackLimits);
          }
          return;
        }
        const { data: plan, error: planError } = await supabase
          .from('plans')
          .select('*')
          .eq('id', subscription.plan_id)
          .single();
        if (planError) throw planError;
        const name = plan?.name || 'Free';
        const lower = name.toLowerCase();
        let tier = { id: 'free', label: name, fans: 200 };
        if (lower.includes('growth')) tier = { ...tier, id: 'growth', fans: 2500 };
        else if (lower.includes('pro')) tier = { ...tier, id: 'pro', fans: 10000 };
        if (active) {
          setPlanId(tier.id);
          setLimits(tier);
        }
      } catch (e) {
        if (active) {
          setPlanId('free');
          setLimits(fallbackLimits);
        }
      } finally {
        if (active) setLoadingPlan(false);
      }
    };
    loadPlan();
    return () => { active = false; };
  }, [user?.id]);

  useEffect(() => {
    const fetchFans = async () => {
      try {
        setLoading(true);
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('fans')
          .eq('id', user.id)
          .single();

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          return;
        }

        if (profiles?.fans) {
          setFans(profiles.fans.map((fanString, index) => {
            const fan = JSON.parse(fanString);
            return {
              id: fan.id || index + 1,
              name: fan.name || '',
              email: fan.email || '',
              phone: fan.phone || '',
              joinDate: fan.joinDate || new Date().toISOString().split('T')[0],
              clicks: typeof fan.clicks === 'number' ? fan.clicks : 0,
              role: fan.role || (typeof fan.clicks === 'number' ? (fan.clicks >= 100 ? 'megafan' : fan.clicks >= 50 ? 'superfan' : 'fan') : 'fan'),
            };
          }));
        }
      } catch (error) {
        toast.error('Failed to load fans');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchFans();
    }
  }, [user?.id]);

  const filteredFans = fans.filter(fan => {
    const matchesSearch = fan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fan.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getRoleIcon = (role) => {
    switch (role) {
      case 'megafan':
        return <Star className="w-4 h-4 text-yellow-500" />;
      case 'superfan':
        return <UserCheck className="w-4 h-4 text-purple-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'megafan':
        return 'bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-700';
      case 'superfan':
        return 'bg-purple-50 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-700';
      default:
        return 'bg-gray-50 text-gray-800 border-gray-200 dark:bg-gray-700/20 dark:text-gray-400 dark:border-gray-600';
    }
  };

  const handleAddFan = async () => {
    // Enforce plan fan limit before any validation work beyond required name
    if (fans.length >= (limits?.fans ?? 0)) {
      toast.error(`Fan limit reached (${limits.fans.toLocaleString()})`, {
        description: `Your ${limits.label || 'current'} plan allows up to ${limits.fans.toLocaleString()} fans. Upgrade to grow further.`
      });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;

    if (!newFan.name) {
      toast.error('Please fill in the full name', {
        description: 'Full name is required to add a fan.'
      });
      return;
    }

    if (newFan.email && !emailRegex.test(newFan.email)) {
      toast.error('Invalid email format', {
        description: 'Please provide a valid email address.'
      });
      return;
    }

    if (newFan.phone && !phoneRegex.test(newFan.phone)) {
      toast.error('Invalid phone number format', {
        description: 'Phone number must start with a valid country code (e.g., +1, +44).' 
      });
      return;
    }

    try {
      setAdding(true);
      const fan = {
        id: fans.length + 1,
        name: newFan.name,
        email: newFan.email || '',
        phone: newFan.phone || '',
        joinDate: new Date().toISOString().split('T')[0],
        clicks: 0,
        role: 'fan'
      };

      const updatedFans = [...fans, fan];
      setFans(updatedFans);

      const { error } = await supabase
        .from('profiles')
        .update({ fans: updatedFans.map(f => JSON.stringify(f)) })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating fans:', error);
        toast.error('Failed to add fan. Please try again.');
        setFans(fans); // Revert on error
        return;
      }

      toast.success(`Fan "${newFan.name}" added successfully!`, {
        description: `${newFan.email || 'No email provided'} has been added to your fanbase.`
      });
      setIsAddFanOpen(false);
      setNewFan({ name: '', email: '', phone: '' });
    } catch (error) {
      toast.error('Failed to add fan');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteFan = async () => {
    if (!fanToDelete) return;

    try {
      const updatedFans = fans.filter((f) => f.id !== fanToDelete.id);
      setFans(updatedFans);

      const { error } = await supabase
        .from('profiles')
        .update({ fans: updatedFans.map(f => JSON.stringify(f)) })
        .eq('id', user.id);

      if (error) {
        console.error('Error deleting fan:', error);
        toast.error('Failed to delete fan. Please try again.');
        return;
      }

      toast.success(`Fan "${fanToDelete.name}" deleted successfully!`);
      setIsDeleteDialogOpen(false);
      setFanToDelete(null);
      if (selectedFan?.id === fanToDelete.id) {
        setSelectedFan(null);
      }
    } catch (error) {
      toast.error('Failed to delete fan');
    }
  };

  const exportFansToCSV = () => {
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Join Date', 'Role', 'Clicks'].join(','),
      ...fans.map(fan => [
        fan.name,
        fan.email || 'N/A',
        fan.phone || 'N/A',
        fan.joinDate,
        fan.role,
        fan.clicks
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'fans.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Fan list exported successfully!');
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone) ? phone : 'Invalid Format';
  };

  // Calculate stats
  const totalFans = fans.length;
  const megaFans = fans.filter(f => f.role === 'megafan').length;
  const superFans = fans.filter(f => f.role === 'superfan').length;
  const totalClicks = fans.reduce((sum, fan) => sum + (fan.clicks || 0), 0);

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Fan Management</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your fanbase and track engagement
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportFansToCSV}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-white/10 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => setIsAddFanOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Fan
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {totalFans}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                Total Fans
                {typeof limits?.fans === 'number' && (
                  <span className="text-xs text-gray-500 dark:text-gray-500 font-normal">/ {limits.fans.toLocaleString()}</span>
                )}
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-50 dark:bg-purple-500/10 rounded-lg flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {superFans}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Super Fans
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-yellow-50 dark:bg-yellow-500/10 rounded-lg flex items-center justify-center">
                <Star className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {megaFans}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Mega Fans
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {totalClicks}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total Clicks
              </p>
            </div>
          </div>
        </div>

        {/* Search */}
          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search fans by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>
        </div>

        {/* Fan List */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Fans ({filteredFans.length})
                </h2>
              </div>
              
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading && (
                  <div className="p-6 text-center">
                    <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">Loading fans...</p>
                  </div>
                )}

                {!loading && filteredFans.length === 0 && (
                  <div className="p-6 text-center">
                    <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">No fans found</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">Add your first fan to get started</p>
                  </div>
                )}

                {!loading && filteredFans.map((fan) => (
                  <div
                    key={fan.id}
                    className={`p-4 cursor-pointer transition-colors ${
                      selectedFan?.id === fan.id ? "bg-blue-50 dark:bg-blue-900/20 border-r-4 border-blue-500" : ""
                    }`}
                    onClick={() => setSelectedFan(fan)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {fan.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-gray-900 dark:text-white truncate">{fan.name}</h3>
                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getRoleColor(fan.role)}`}>
                              {getRoleIcon(fan.role)}
                              {fan.role.charAt(0).toUpperCase() + fan.role.slice(1)}
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{fan.email || 'No email'}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">{fan.clicks} clicks • Joined {new Date(fan.joinDate).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFanToDelete(fan);
                          setIsDeleteDialogOpen(true);
                        }}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Fan Details */}
          <div>
            <div className="rounded-xl shadow-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Fan Details</h2>
              </div>
              
              {selectedFan ? (
                <div className="p-6 space-y-4">
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-3">
                      {selectedFan.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{selectedFan.name}</h3>
                    <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border mt-2 ${getRoleColor(selectedFan.role)}`}>
                      {getRoleIcon(selectedFan.role)}
                      {selectedFan.role.charAt(0).toUpperCase() + selectedFan.role.slice(1)}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Email</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{selectedFan.email || 'Not provided'}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Phone</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{formatPhoneNumber(selectedFan.phone)}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Join Date</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{new Date(selectedFan.joinDate).toLocaleDateString()}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Total Clicks</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{selectedFan.clicks || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center">
                  <User className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">Select a fan to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add Fan Modal */}
        {isAddFanOpen && (
          <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto bg-white dark:bg-black border border-gray-200 dark:border-white/10 hover:border-white/20">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Add New Fan</h2>
                  <button
                    onClick={() => setIsAddFanOpen(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={newFan.name}
                      onChange={(e) => setNewFan((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="Enter fan's full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Email Address (Optional)
                    </label>
                    <input
                      type="email"
                      value={newFan.email}
                      onChange={(e) => setNewFan((prev) => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="Enter email address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Phone Number (Optional)
                    </label>
                    <input
                      type="tel"
                      value={newFan.phone}
                      onChange={(e) => setNewFan((prev) => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="Enter phone number with country code"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setIsAddFanOpen(false)}
                    className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddFan}
                    disabled={!newFan.name || adding}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {adding ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    {adding ? 'Adding...' : 'Add Fan'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteDialogOpen && (
          <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-black rounded-xl shadow-xl max-w-sm w-full border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Delete Fan</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Are you sure you want to delete <strong>{fanToDelete?.name}</strong>? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setIsDeleteDialogOpen(false);
                      setFanToDelete(null);
                    }}
                    className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteFan}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Fans;